import { parseRoutingFromEnv, resolveWriteRepo } from './writeRouting';

export class CompositeCityRepository {
  constructor({ firestoreRepo, apiRepo, writeRouting = {} }) {
    this.firestoreRepo = firestoreRepo;
    this.apiRepo = apiRepo;
    this.writeRouting = {
      defaultTarget: writeRouting.defaultTarget || 'firestore',
      operationTargets: writeRouting.operationTargets || {}
    };
  }

  writeRepoFor(operation) {
    return resolveWriteRepo({
      routing: this.writeRouting,
      operation,
      firestoreRepo: this.firestoreRepo,
      apiRepo: this.apiRepo,
      scopeName: 'CityRepository'
    });
  }

  loadCities(includeNonPublic) {
    return this.firestoreRepo.loadCities(includeNonPublic);
  }

  loadCity(cityId, useCache) {
    return this.firestoreRepo.loadCity(cityId, useCache);
  }

  onCity(cityId, handler) {
    return this.firestoreRepo.onCity(cityId, handler);
  }

  searchCities(query) {
    return this.firestoreRepo.searchCities(query);
  }

  geocodeAddress(address, options) {
    return this.firestoreRepo.geocodeAddress(address, options);
  }

  updateCity(cityId, patch, options) {
    return this.writeRepoFor('updateCity').updateCity(cityId, patch, options);
  }

  updateCityLicenseExpirationPolicy(cityId, payload) {
    return this.writeRepoFor('updateCityLicenseExpirationPolicy')
      .updateCityLicenseExpirationPolicy(cityId, payload);
  }

  updateCityLicenseWorkflowStatuses(cityId, payload) {
    return this.writeRepoFor('updateCityLicenseWorkflowStatuses')
      .updateCityLicenseWorkflowStatuses(cityId, payload);
  }

  updateCityLicenseApprovalEmailSettings(cityId, payload) {
    return this.writeRepoFor('updateCityLicenseApprovalEmailSettings')
      .updateCityLicenseApprovalEmailSettings(cityId, payload);
  }

  updateCityLicenseStatusEmails(cityId, payload) {
    return this.writeRepoFor('updateCityLicenseStatusEmails')
      .updateCityLicenseStatusEmails(cityId, payload);
  }

  previewCityLicenseStatusEmailDraft(cityId, payload) {
    return this.apiRepo.previewCityLicenseStatusEmailDraft(cityId, payload);
  }

  // Logo bytes must pass server validation; always use the API for this design.
  updateCityPrintableLicense(cityId, payload) {
    return this.apiRepo.updateCityPrintableLicense(cityId, payload);
  }

  previewCityPrintableLicense(cityId, payload) {
    return this.apiRepo.previewCityPrintableLicense(cityId, payload);
  }

  fetchFieldCounts(collectionName, fieldName, fieldValue, options) {
    return this.firestoreRepo.fetchFieldCounts(collectionName, fieldName, fieldValue, options);
  }

  fetchDefaultFieldCounts(collectionName, fieldNames, options) {
    return this.firestoreRepo.fetchDefaultFieldCounts(collectionName, fieldNames, options);
  }

  ensureDefaultValues(cityId, namespace) {
    return this.apiRepo.ensureDefaultValues(cityId, namespace);
  }

  sendErrorReport(cityId, errorType, errorData) {
    return this.writeRepoFor('sendErrorReport').sendErrorReport(cityId, errorType, errorData);
  }

  queueCityCheck(cityId) {
    return this.writeRepoFor('queueCityCheck').queueCityCheck(cityId);
  }
}

export function getCityWriteRoutingFromEnv(env = process.env) {
  const routing = parseRoutingFromEnv({
    env,
    defaultTargetKeys: [
      'VUE_APP_CITY_WRITE_REPOSITORY',
      'VUE_APP_CITY_WRITE_TARGET'
    ],
    operationTargetsKeys: [
      'VUE_APP_CITY_WRITE_REPOSITORY_BY_OPERATION'
    ]
  });

  // Assigned staff use the validated API for policy changes; direct city administration is admin-only.
  for (const operation of [
    'updateCityLicenseExpirationPolicy',
    'updateCityLicenseWorkflowStatuses',
    'updateCityLicenseApprovalEmailSettings',
    'updateCityLicenseStatusEmails'
  ]) {
    routing.operationTargets[operation] = 'api';
  }

  return routing;
}
